<template>
  <div class="member-list-page">
    <header class="member-list-header">
      <button
        type="button"
        class="back-button"
        :aria-label="$t('memberList.back')"
        @click="$router.back()"
      >
        <span aria-hidden="true" />
      </button>
      <h1>{{ $t(titleKey) }}</h1>
    </header>

    <nav
      class="distance-filters"
      :aria-label="$t('memberList.distanceFilter')"
    >
      <button
        v-for="filter in filters"
        :key="filter.value || 'all'"
        type="button"
        :class="{ active: activeDistance === filter.value }"
        @click="selectDistance(filter.value)"
      >
        {{ $t(filter.label) }}
      </button>
    </nav>

    <main
      ref="scrollContent"
      class="member-list-content"
    >
      <van-empty
        v-if="empty"
        :description="$t('memberList.empty')"
      />

      <van-list
        v-else
        v-model="loading"
        :finished="finished"
        :immediate-check="true"
        :offset="100"
        :loading-text="$t('pro2.loading')"
        class="member-list"
        @load="loadMembers"
      >
        <div class="member-grid">
          <article
            v-for="member in members"
            :key="member.id"
            class="member-card"
            @click="openProfile(member.id)"
          >
            <div class="member-photo">
              <img
                :src="imageUrl(member.pic)"
                :alt="member.name"
              >
              <span
                class="favorite-mark"
                aria-hidden="true"
              >♥</span>
              <span class="same-city">{{ $t('memberList.sameCity') }}</span>
            </div>

            <div class="member-details">
              <div class="member-summary">
                <img
                  class="member-avatar"
                  :src="imageUrl(member.pic)"
                  alt=""
                >
                <span class="member-name">{{ member.name }}</span>
                <span class="member-distance">
                  <van-icon
                    name="location"
                    size="12"
                  />
                  {{ distanceText(member.distance_km) }}</span>
              </div>
              <div
                class="member-rating"
                :aria-label="ratingText(member.fee)"
              >
                <span
                  v-for="star in 5"
                  :key="star"
                  :class="{ filled: star <= member.fee }"
                  aria-hidden="true"
                >★</span>
              </div>
              <button
                type="button"
                class="book-button"
                @click.stop="openProfile(member.id)"
              >
                {{ $t('memberList.book') }}
              </button>
            </div>
          </article>
        </div>
      </van-list>
    </main>
  </div>
</template>

<script>
import Vue from "vue";
import { Empty, List } from "vant";
import api from "../interface/index";
import Fetch from "../utils/fetch";

Vue.use(Empty).use(List);

export default {
  name: "MemberList",
  props: {
    gender: {
      type: String,
      required: true,
      validator(value) {
        return ["female", "male"].indexOf(value) !== -1;
      }
    },
    titleKey: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      activeDistance: null,
      page: 1,
      members: [],
      loading: false,
      finished: false,
      empty: false,
      requestLock: false,
      requestVersion: 0,
      filters: [
        { value: null, label: "memberList.all" },
        { value: 3, label: "memberList.within3km" },
        { value: 5, label: "memberList.within5km" },
        { value: 10, label: "memberList.within10km" },
        { value: 15, label: "memberList.within15km" },
        { value: 30, label: "memberList.within30km" },
        { value: 50, label: "memberList.within50km" }
      ]
    };
  },
  methods: {
    selectDistance(distance) {
      if (this.activeDistance === distance) {
        return;
      }

      this.activeDistance = distance;
      this.page = 1;
      this.members = [];
      this.finished = false;
      this.empty = false;
      this.requestLock = false;
      this.loading = false;
      this.requestVersion += 1;

      this.$nextTick(() => {
        this.resetScrollPosition();
        this.loadMembers();
      });
    },
    loadMembers() {
      if (this.requestLock || this.finished) {
        this.loading = false;
        return;
      }

      this.requestLock = true;
      this.loading = true;
      const requestVersion = this.requestVersion;
      const params = {
        page: this.page,
        gender: this.gender
      };

      if (this.activeDistance !== null) {
        params.distance_km = this.activeDistance;
      }

      Fetch("/index/girls", params).then((response) => {
        if (requestVersion !== this.requestVersion) {
          return;
        }

        const data = response.data || {};
        const rows = Array.isArray(data.list) ? data.list : [];

        if (rows.length > 0) {
          this.members = this.members.concat(rows);
          this.page += 1;
        }

        this.empty = this.members.length === 0;
        this.finished = !data.has_more || rows.length === 0;
        this.loading = false;
        this.requestLock = false;
      }).catch(() => {
        if (requestVersion !== this.requestVersion) {
          return;
        }

        this.loading = false;
        this.requestLock = false;
        this.finished = true;
        this.empty = this.members.length === 0;
      });
    },
    resetScrollPosition() {
      const scrollContent = this.$refs.scrollContent;

      if (scrollContent) {
        scrollContent.scrollTop = 0;
      }
    },
    openProfile(id) {
      this.$router.push("/vprofile/" + id);
    },
    imageUrl(imagePath) {
      if (!imagePath || /^(?:https?:)?\/\//.test(imagePath) || imagePath.indexOf("data:") === 0) {
        return imagePath;
      }

      const baseUrl = (api.commonUrl || "").replace(/\/$/, "");
      const relativePath = imagePath.charAt(0) === "/" ? imagePath : "/" + imagePath;

      return baseUrl + relativePath;
    },
    distanceText(distance) {
      return this.$t("memberList.withinDistance", { distance: distance || 0 });
    },
    ratingText(rating) {
      return this.$t("memberList.rating", { rating: rating || 0 });
    }
  }
};
</script>

<style lang="less" scoped>
.member-list-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  padding: 12px 18px 0;
  overflow: hidden;
  box-sizing: border-box;
  background: #fbf9fc url(../views/images/ban5/g.png) no-repeat top center / 100% auto;
}

.member-list-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
}

.member-list-header h1 {
  margin: 0;
  color: #000;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.back-button {
  position: absolute;
  top: 50%;
  left: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  transform: translateY(-50%);
}

.back-button span {
  position: absolute;
  top: 7px;
  left: 9px;
  width: 8px;
  height: 8px;
  border-bottom: 1px solid #121212;
  border-left: 1px solid #121212;
  transform: rotate(45deg);
}

.distance-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 15px -18px 10px;
  padding: 0 18px;
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.distance-filters::-webkit-scrollbar {
  display: none;
}

.distance-filters button {
  flex: 0 0 auto;
  height: 30px;
  padding: 0 10px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #606060;
  font-size: 12px;
  font-weight: 600;
  line-height: 30px;
  text-align: center;
  white-space: nowrap;
}

.distance-filters button:first-child {
  padding-right: 8px;
  padding-left: 8px;
}

.distance-filters button.active {
  background: #f8328c;
  color: #fff;
}

.member-list-content {
  flex: 1;
  min-height: 0;
  padding-bottom: 72px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.member-list {
  min-height: 100%;
}

.member-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 10px;
  row-gap: 14px;
}

.member-card {
  min-width: 0;
}

.member-photo {
  position: relative;
  width: 100%;
  padding-top: 100%;
  overflow: hidden;
  border-radius: 5px;
  background: #f2f2f2;
}

.member-photo > img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.favorite-mark {
  position: absolute;
  top: 9px;
  left: 9px;
  display: block;
  width: 25px;
  height: 25px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  color: #f83e93;
  font-size: 20px;
  line-height: 25px;
  text-align: center;
}

.same-city {
  position: absolute;
  top: 0;
  right: 0;
  height: 24px;
  padding: 0 6px;
  border-radius: 0 5px 0 5px;
  background: #f8328c;
  color: #fff;
  font-size: 12px;
  line-height: 24px;
}

.member-details {
  padding-top: 5px;
}

.member-summary {
  display: flex;
  align-items: center;
  min-width: 0;
  height: 25px;
}

.member-avatar {
  flex: 0 0 auto;
  width: 23px;
  height: 23px;
  margin-right: 4px;
  border-radius: 50%;
  object-fit: cover;
}

.member-name,
.member-distance {
  overflow: hidden;
  color: #111;
  font-size: 10px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.member-name {
  min-width: 0;
  flex: 1;
}

.member-distance {
  // max-width: 54px;
  margin-left: 3px;
}

// .member-distance::before {
//   content: "●";
//   margin-right: 2px;
//   color: #142746;
//   font-size: 10px;
// }

.member-rating {
  height: 35px;
  color: #d4dbe5;
  font-size: 14px;
  letter-spacing: 1px;
  line-height: 35px;
}

.member-rating .filled {
  color: #ffc719;
}

.book-button {
  display: block;
  width: 100%;
  height: 26px;
  padding: 0 5px;
  overflow: hidden;
  border: 0;
  border-radius: 14px;
  background: linear-gradient(100deg, #df4ab7 0%, #6d3bc4 100%);
  color: #fff;
  font-size: 12px;
  line-height: 26px;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
